import { defineStore } from 'pinia';
import { ref } from 'vue';
import Client from '@/lib/client';

// Initialize API client
const apiClient = new Client('web', 'v2');

// Request controllers for cancellation
let reportsAbortController: AbortController | null = null;
let filtersAbortController: AbortController | null = null;

// Track ongoing search requests
let currentSearchRequest: Promise<any> | null = null;
let searchRequestId = 0;

// Cleanup function to abort any pending requests
function cleanupPendingRequests() {
  if (reportsAbortController) {
    reportsAbortController.abort();
    reportsAbortController = null;
  }
  if (filtersAbortController) {
    filtersAbortController.abort();
    filtersAbortController = null;
  }
  currentSearchRequest = null;
}

// API Endpoints
const API_BASE_PATH = '_ah/api/web/v2';

const API_ENDPOINTS = {
  reports: `${API_BASE_PATH}/reports`,
  filters: `${API_BASE_PATH}/reports/filters`,
  reportDetail: (slug: string) => `${API_BASE_PATH}/reports/${slug}`,
  reportGrade: (slug: string, grade: string) =>
    `${API_BASE_PATH}/reports/${slug}/${grade}`,
  reportGateway: (slug: string, grade: string, gateway: string) =>
    `${API_BASE_PATH}/reports/${slug}/${grade}/${gateway}`,
};

// Add filter types
export interface FilterOption {
  value: string;
  label: string;
  name?: string;
  disabled?: boolean;
}

export interface FilterGroup {
  id: string;
  name: string;
  options: FilterOption[];
  slug?: string;
  label?: string;
  value?: string;
  multiple?: boolean;
  sequential?: boolean;
  required?: boolean;
  disabled?: boolean;
  description?: string;
}

// NEW TYPE DEFINITIONS START
export interface GradeInfo {
  slug: string;
  shortcode: string;
  name: string;
  title: string;
  order: number;
  gradeLevel: string;
  label?: string;
}

export interface ReviewStatus {
  slug: string;
  name: string;
  title: string;
}

export interface GatewayScore {
  slug: string;
  label: string;
  name: string;
  title: string;
  score: string;
  review_status: ReviewStatus;
  order: number;
  failed_non_negotiable: boolean;
}

export interface ReportCardItem {
  grade: GradeInfo;
  gateways: GatewayScore[];
}

export interface CriterionScore {
  grade: GradeInfo;
  score: string;
  review_status: ReviewStatus;
  failed_non_negotiable: boolean;
}

export interface Criterion {
  slug: string;
  label: string;
  non_negotiable: boolean;
  scores: CriterionScore[];
  order: number;
  summary?: string;
}

export interface CriterionGroup {
  slug: string;
  title: string;
  summary: string;
  criteria: Criterion[];
  order: number;
}

export interface Overview {
  name?: string;
  title?: string;
  subject?: string;
  summary: string;
  report_cards: ReportCardItem[];
  criteria_groups: CriterionGroup[];
}

// NEW TYPES FOR GATEWAY SNAPSHOTS
export interface GatewaySnapshotScore {
  grade: GradeInfo;
  score: string;
  review_status: string;
  failed_non_negotiable?: boolean;
}

export interface GatewaySnapshotItem {
  slug: string;
  label: string;
  name: string;
  title: string;
  scores: GatewaySnapshotScore[];
  order: number;
  grades?: GradeInfo[] | null;
  failed_non_negotiable: boolean;
}
// END NEW TYPES FOR GATEWAY SNAPSHOTS

export type Report = {
  slug: string;
  title: string;
  publisher: string | null;
  cover: string | null;
  subject: any | null;
  formats: string[];
  grades: string;
  release: string | null;
  versions: string[];
  gateways: GatewayScore[];
  details: Array<{
    name: string;
    value: string;
  }> | null;
  groups: string[];
  alerts: Array<{
    class: string;
    content: string;
  }> | null;
  copyright: string;
  overviews: Overview[] | null;
  gateway_snapshots: GatewaySnapshotItem[] | null;
  report_grades?: GradeInfo[] | null;
  downloads?: Array<{
    uri: string;
    name: string;
    extension: string;
    mime_type: string;
  }> | null;
  review_criteria?: {
    uri: string;
    name: string;
    extension: string;
    mime_type: string;
  } | null;
  evidence_guide?: {
    uri: string;
    name: string;
    extension: string;
    mime_type: string;
  } | null;
  gateway_overviews?: Array<{
    slug: string;
    label: string;
    name: string;
    title: string;
    score: string;
    review_status: ReviewStatus;
    order: number;
    grades?: any;
    failed_non_negotiable: boolean;
    is_mll: boolean;
    criterion_overviews?: Array<{
      slug: string;
      label: string;
      name: string;
      indicators?: Array<{
        slug: string;
        label: string;
        rating?: {
          score: number;
          total: number;
          review_status: ReviewStatus;
        };
      }>;
    }>;
  }> | null;
  summary?: string | null;
};

interface ApiResponse<T> {
  items?: T[];
  data?: T;
  total?: number;
  gateway_snapshots?: any[];
  gateway_overviews?: any;
  gateways?: any[];
}

// Define store
export const useReportsStore = defineStore('reports', {
  state: () => ({
    reports: [] as Report[],
    loading: false,
    error: null as string | null,
    sortOption: 'published',
    searchQuery: '' as string,
    selectedSubjects: [] as string[],
    selectedFormats: [] as string[],
    selectedGrades: [] as string[],
    selectedAlignment: [] as string[],
    selectedUsability: [] as string[],
    selectedVersions: [] as string[],
    selectedPublishers: [] as string[],
    selectedSupportingDocuments: [] as string[],
    additionalFilters: new Map<string, string[]>(),
    currentPage: 1,
    itemsPerPage: 12,
    totalItems: 0,
    filters: [] as FilterGroup[],
    gradeRange: { start: 0, end: 12 } as { start: number; end: number },
    reportsRequestInProgress: false,
    filtersRequestInProgress: false,
    lastFetchTimestamp: 0,
    minFetchInterval: 300,
    initializing: false,
  }),

  actions: {
    buildFilterPayload(): { filters?: Record<string, any>; search?: string } {
      const payload: { filters?: Record<string, any>; search?: string } = {};
      const filters: Record<string, any> = {};

      const trimmedSearch = (this.searchQuery || '').trim();
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      if (this.selectedSubjects.length > 0)
        filters.subject = this.selectedSubjects[0];
      if (this.selectedGrades.length > 0)
        filters['grade-levels'] = this.selectedGrades;
      if (this.selectedFormats.length > 0)
        filters.formats = this.selectedFormats;
      if (this.selectedVersions.length > 0)
        filters.versions = this.selectedVersions;

      // Send alignment and usability as their own filters
      if (this.selectedAlignment.length > 0)
        filters.alignment = this.selectedAlignment;
      if (this.selectedUsability.length > 0)
        filters.usability = this.selectedUsability;

      if (this.selectedPublishers.length > 0)
        filters.publishers = this.selectedPublishers;
      if (this.selectedSupportingDocuments.length > 0)
        filters['supporting-documents'] = this.selectedSupportingDocuments;

      for (const [filterType, values] of this.additionalFilters) {
        if (values.length > 0) {
          filters[filterType] = values;
        }
      }

      if (Object.keys(filters).length > 0) {
        payload.filters = filters;
      }

      return payload;
    },

    async initialize() {
      if (this.initializing) return;
      this.initializing = true;
      cleanupPendingRequests();
      if (
        this.reportsRequestInProgress ||
        this.filtersRequestInProgress ||
        (this.filters.length > 0 && this.reports.length > 0)
      ) {
        if (!this.reportsRequestInProgress && !this.filtersRequestInProgress)
          this.loading = false;
        this.initializing = false;
        return;
      }

      this.loading = true;
      this.error = null;
      try {
        if (!import.meta.env.VITE_APP_API_URL) {
          throw new Error('API URL not configured.');
        }
        if (this.filters.length === 0) {
          await this.fetchFilters(true);
        }
        if (this.reports.length === 0) {
          await this.fetchReports(true);
        }
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : 'Failed to initialize store';
      } finally {
        this.loading = false;
        this.initializing = false;
      }
    },

    cleanup() {
      cleanupPendingRequests();
    },

    async fetchFilters(isInitialization: boolean = false) {
      if (!isInitialization && filtersAbortController)
        filtersAbortController.abort();
      if (!isInitialization) filtersAbortController = new AbortController();

      if (this.filtersRequestInProgress && !isInitialization) return;

      this.filtersRequestInProgress = true;
      if (!isInitialization) this.loading = true;
      this.error = null;

      try {
        const payload = this.buildFilterPayload();
        const options = { signal: filtersAbortController?.signal };
        const response = (await apiClient.execute(
          API_ENDPOINTS.filters,
          payload,
          'POST',
          options,
        )) as ApiResponse<FilterGroup>;

        if (response.items) {
          this.filters = response.items.map((filter) => ({
            id: filter.slug || filter.id,
            name:
              filter.label ||
              filter.name ||
              this.getFilterNameFromId(filter.slug || filter.id),
            options: filter.options.map((option) => ({
              value: option.value,
              label: option.label || option.name || option.value,
              disabled: option.disabled || false,
            })),
            multiple: filter.multiple === true,
            sequential: filter.sequential,
            required: filter.required,
            description: filter.description,
            slug: filter.slug,
            label: filter.label,
          }));
        } else {
          this.filters = [];
        }
      } catch (err) {
        if (err && (err as any).name === 'AbortError') {
          return;
        }
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch filters';
      } finally {
        this.filtersRequestInProgress = false;
        if (!isInitialization) this.loading = false;
        if (!isInitialization) filtersAbortController = null;
      }
    },

    async fetchReports(isInitialization: boolean = false) {
      if (this.initializing && !isInitialization) return;
      if (reportsAbortController) {
        reportsAbortController.abort();
      }

      reportsAbortController = new AbortController();

      if (!isInitialization) {
        this.loading = true;
      }
      this.error = null;
      this.reportsRequestInProgress = true;

      try {
        const sortConfig = {
          'Recently Published': { order: 'published', direction: 'desc' },
          Alphabetical: { order: 'alpha', direction: 'asc' },
          Copyright: { order: 'edition', direction: 'desc' },
        };
        const currentSortParams = sortConfig[
          this.sortOption as keyof typeof sortConfig
        ] || { order: 'published', direction: 'desc' };
        const payload = this.buildFilterPayload();

        const requestBody = {
          limit: this.itemsPerPage,
          offset: (this.currentPage - 1) * this.itemsPerPage,
          order: currentSortParams.order,
          direction: currentSortParams.direction,
          ...payload,
        };

        const options = {
          signal: reportsAbortController.signal,
          timeout: process.env.NODE_ENV !== 'production' ? 90000 : 30000,
        };

        const response = (await apiClient.execute(
          API_ENDPOINTS.reports,
          requestBody,
          'POST',
          options,
        )) as ApiResponse<Report>;

        if (!response) throw new Error('Invalid API response received');
        if (response.items === null && typeof response.total === 'number') {
          this.reports = [];
          this.totalItems = response.total;
        } else {
          this.reports = (response.items || []).map((report: Report) => ({
            ...report,
            cover: report.cover || null,
            formats: Array.isArray(report.formats) ? report.formats : [],
            versions: Array.isArray(report.versions) ? report.versions : [],
            details: Array.isArray(report.details) ? report.details : [],
            alerts: Array.isArray(report.alerts) ? report.alerts : null,
          }));
          this.totalItems = response.total || 0;
        }
      } catch (err) {
        if (err && (err as any).name === 'AbortError') {
          return;
        }
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch reports';
      } finally {
        this.reportsRequestInProgress = false;
        if (!isInitialization) {
          this.loading = false;
        }
        if (reportsAbortController && reportsAbortController.signal.aborted) {
          reportsAbortController = null;
        }
      }
    },

    setPage(page: number) {
      if (page === this.currentPage) return;
      this.currentPage = page;
      this.fetchReports();
    },

    setItemsPerPage(items: number) {
      if (items === this.itemsPerPage) return;
      this.itemsPerPage = items;
      this.currentPage = 1;
      this.fetchReports();
    },

    async fetchReportDetail(slug: string) {
      this.loading = true;
      this.error = null;
      try {
        const response = (await apiClient.execute(
          API_ENDPOINTS.reportDetail(slug),
        )) as ApiResponse<Report>;
        // Transform the response data - gateway_snapshots is already in response.data
        if (response.data) {
          // Since gateway_snapshots is already in response.data, just return the data directly
          const transformedData = response.data;

          return transformedData;
        }
        return response.data;
      } catch (err) {
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch report detail';
        // Error will be thrown and handled by caller
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchReportGrade(slug: string, grade: string) {
      this.loading = true;
      this.error = null;
      try {
        const response = (await apiClient.execute(
          API_ENDPOINTS.reportGrade(slug, grade),
        )) as ApiResponse<any>;

        if (response.data) {
          const gradeData = response.data;

          const transformedReport: Report = {
            slug: slug,
            title: gradeData.title || `${slug} - Grade ${grade}`,
            publisher: gradeData.publisher || null,
            cover: gradeData.cover || null,
            subject: gradeData.subject || null,
            formats: Array.isArray(gradeData.formats) ? gradeData.formats : [],
            grades: gradeData.grades || grade,
            release: gradeData.release || null,
            versions: Array.isArray(gradeData.versions)
              ? gradeData.versions
              : [],
            gateways: Array.isArray(gradeData.gateways)
              ? gradeData.gateways.map((gw: any) => ({
                  ...gw,
                  review_status: gw.review_status || gw.status,
                }))
              : [],
            details: Array.isArray(gradeData.details)
              ? gradeData.details
              : null,
            groups: Array.isArray(gradeData.groups) ? gradeData.groups : [],
            alerts: Array.isArray(gradeData.alerts) ? gradeData.alerts : null,
            copyright: gradeData.copyright || '',
            overviews: null,
            gateway_snapshots: null,
            report_grades: gradeData.grade ? [gradeData.grade] : null,
            downloads: gradeData.downloads || null,
            review_criteria: gradeData.review_criteria || null,
            evidence_guide: gradeData.evidence_guide || null,
            gateway_overviews: gradeData.gateway_overviews || null,
            summary: gradeData.summary || null,
          };

          return transformedReport;
        }

        return undefined;
      } catch (err) {
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch report grade';
        // Error will be thrown and handled by caller
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async fetchReportGateway(slug: string, grade: string, gateway: string) {
      this.loading = true;
      this.error = null;
      try {
        const response = (await apiClient.execute(
          API_ENDPOINTS.reportGateway(slug, grade, gateway),
        )) as ApiResponse<any>;

        if (response.data) {
          return response.data;
        }
        return null;
      } catch (err) {
        this.error =
          err instanceof Error ? err.message : 'Failed to fetch report gateway';
        // Error will be thrown and handled by caller
        throw err;
      } finally {
        this.loading = false;
      }
    },

    setSortOption(option: string) {
      if (this.sortOption === option) return;
      this.sortOption = option;
      this.currentPage = 1;
      this.fetchReports();
    },

    toggleSubject(subject: string) {
      const index = this.selectedSubjects.indexOf(subject);
      if (index === -1) {
        this.selectedSubjects.push(subject);
      } else {
        this.selectedSubjects.splice(index, 1);
      }
    },

    setSubject(subject: string, selected: boolean) {
      const subjectFilter = this.filters.find(
        (f) => f.id === 'subject' || f.slug === 'subject',
      );
      const isMultiple = subjectFilter?.multiple === true;
      const upperCaseSubject = subject.toUpperCase();
      const currentIndex = this.selectedSubjects.findIndex(
        (subj) => subj.toUpperCase() === upperCaseSubject,
      );
      let stateChanged = false;

      if (selected) {
        if (isMultiple) {
          if (currentIndex === -1) {
            this.selectedSubjects.push(subject);
            stateChanged = true;
          }
        } else {
          if (
            currentIndex === -1 ||
            this.selectedSubjects.length > 1 ||
            this.selectedSubjects[0] !== subject
          ) {
            this.selectedSubjects = [subject];
            stateChanged = true;
          }
        }
      } else {
        if (currentIndex !== -1) {
          this.selectedSubjects.splice(currentIndex, 1);
          stateChanged = true;
        }
      }
      if (stateChanged) this.currentPage = 1;
    },

    setFormat(formatValue: string, selected: boolean) {
      const filterDef = this.filters.find(
        (f) => f.id === 'formats' || f.slug === 'formats',
      );
      const isMultiple = filterDef?.multiple === true;
      const index = this.selectedFormats.indexOf(formatValue);
      let stateChanged = false;

      if (selected) {
        if (isMultiple) {
          if (index === -1) {
            this.selectedFormats.push(formatValue);
            stateChanged = true;
          }
        } else {
          if (
            index === -1 ||
            this.selectedFormats.length > 1 ||
            this.selectedFormats[0] !== formatValue
          ) {
            this.selectedFormats = [formatValue];
            stateChanged = true;
          }
        }
      } else {
        if (index !== -1) {
          this.selectedFormats.splice(index, 1);
          stateChanged = true;
        }
      }
      if (stateChanged) this.currentPage = 1;
    },

    setGrade(gradeValue: string, selected: boolean) {
      const filterDef = this.filters.find(
        (f) => f.id === 'grade-levels' || f.slug === 'grade-levels',
      );
      const isMultiple = filterDef?.multiple === true;
      const index = this.selectedGrades.indexOf(gradeValue);
      let stateChanged = false;

      if (selected) {
        if (isMultiple) {
          if (index === -1) {
            this.selectedGrades.push(gradeValue);
            stateChanged = true;
          }
        } else {
          if (
            index === -1 ||
            this.selectedGrades.length > 1 ||
            this.selectedGrades[0] !== gradeValue
          ) {
            this.selectedGrades = [gradeValue];
            stateChanged = true;
          }
        }
      } else {
        if (index !== -1) {
          this.selectedGrades.splice(index, 1);
          stateChanged = true;
        }
      }
      if (stateChanged) this.currentPage = 1;
    },

    setAlignment(alignmentValue: string, selected: boolean) {
      const index = this.selectedAlignment.indexOf(alignmentValue);
      let stateChanged = false;

      if (selected) {
        if (index === -1) {
          this.selectedAlignment.push(alignmentValue);
          stateChanged = true;
        }
      } else {
        if (index !== -1) {
          this.selectedAlignment.splice(index, 1);
          stateChanged = true;
        }
      }

      if (stateChanged) {
        this.currentPage = 1;
        this.fetchReports();
      }
    },

    setUsability(usabilityValue: string, selected: boolean) {
      const index = this.selectedUsability.indexOf(usabilityValue);
      let stateChanged = false;

      if (selected) {
        if (index === -1) {
          this.selectedUsability.push(usabilityValue);
          stateChanged = true;
        }
      } else {
        if (index !== -1) {
          this.selectedUsability.splice(index, 1);
          stateChanged = true;
        }
      }

      if (stateChanged) {
        this.currentPage = 1;
        this.fetchReports();
      }
    },

    setVersion(version: string, selected: boolean) {
      const versionFilter = this.filters.find(
        (f) => f.id === 'versions' || f.slug === 'versions',
      );
      const isMultiple = versionFilter?.multiple === true;

      const currentIndex = this.selectedVersions.indexOf(version);
      let stateChanged = false;

      if (selected) {
        if (isMultiple) {
          if (currentIndex === -1) {
            this.selectedVersions.push(version);
            stateChanged = true;
          }
        } else {
          if (
            currentIndex === -1 ||
            this.selectedVersions.length > 1 ||
            this.selectedVersions[0] !== version
          ) {
            this.selectedVersions = [version];
            stateChanged = true;
          }
        }
      } else {
        if (currentIndex !== -1) {
          this.selectedVersions.splice(currentIndex, 1);
          stateChanged = true;
        }
      }

      if (stateChanged) {
        this.currentPage = 1;
        this.fetchReports();
      }
    },

    setPublisher(publisher: string, selected: boolean) {
      if (selected) {
        if (!this.selectedPublishers.includes(publisher)) {
          this.selectedPublishers.push(publisher);
        }
      } else {
        this.selectedPublishers = this.selectedPublishers.filter(
          (p) => p !== publisher,
        );
      }
      this.currentPage = 1;
      this.fetchReports();
    },

    setSupportingDocument(document: string, selected: boolean) {
      if (selected) {
        if (!this.selectedSupportingDocuments.includes(document)) {
          this.selectedSupportingDocuments.push(document);
        }
      } else {
        this.selectedSupportingDocuments =
          this.selectedSupportingDocuments.filter((d) => d !== document);
      }
      this.currentPage = 1;
      this.fetchReports();
    },

    setDynamicFilter(filterType: string, value: string, selected: boolean) {
      const currentValues = this.additionalFilters.get(filterType) || [];
      if (selected) {
        if (!currentValues.includes(value)) {
          this.additionalFilters.set(filterType, [...currentValues, value]);
        }
      } else {
        this.additionalFilters.set(
          filterType,
          currentValues.filter((v) => v !== value),
        );
      }
      this.currentPage = 1;
      this.fetchReports();
    },

    getFilterNameFromId(id: string): string {
      const filterNames: Record<string, string> = {
        subject: 'Subject',
        format: 'Format',
        grade: 'Grade Level',
        alignment: 'Alignment Rating',
        usability: 'Usability Rating',
        techInfo: 'Technology Information',
      };
      return filterNames[id] || id;
    },

    formatSubjectName(subject: string): string {
      if (!subject) return 'N/A';
      if (subject.toLowerCase() === 'ela') return 'ELA';
      return subject
        .split(' ')
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
        )
        .join(' ');
    },

    setGradeRange(start: number, end: number) {
      this.gradeRange = { start, end };
    },

    setSelectedFilters(filters: {
      subjects?: string[];
      formats?: string[];
      grades?: string[];
    }) {
      if (filters.subjects) {
        this.selectedSubjects = filters.subjects;
      }
      if (filters.formats) {
        this.selectedFormats = filters.formats;
      }
      if (filters.grades) {
        this.selectedGrades = filters.grades;
      }
      this.fetchReports();
    },

    setSearchQuery(query: string, triggerFetch: boolean = true) {
      const newQuery = query.trim();
      const oldQuery = this.searchQuery;

      this.searchQuery = newQuery;
      this.currentPage = 1;

      if (triggerFetch) {
        this.fetchReports();
      }
    },
  },

  getters: {
    filteredReports: (state) => {
      let filtered = [...state.reports];

      if (state.selectedSubjects.length > 0) {
        filtered = filtered.filter((report) =>
          state.selectedSubjects.includes(report.subject || ''),
        );
      }

      if (state.selectedFormats.length > 0) {
        filtered = filtered.filter((report) =>
          state.selectedFormats.includes(report.formats.join(',') || ''),
        );
      }

      if (state.selectedGrades.length > 0) {
        filtered = filtered.filter((report) => {
          if (!report.grades) return false;
          return state.selectedGrades.includes(report.grades);
        });
      }

      return filtered;
    },
  },
});
